<template>
  <div class="p-6">
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-800">LLM Coaching Configuration</h2>
      <p class="text-gray-600 mt-1">
        Tenant-specific coaching setup: which of <strong>this tenant's</strong> dimensions are coachable,
        profiling and review policy, and prompt behavior. Dimension content is maintained in Dimensions Manager.
        Azure model deployments are set by platform admin (Edit Tenant → LLM Coaching).
      </p>
    </div>

    <Card>
      <template #content>
        <div v-if="loading && !form" class="flex items-center justify-center py-12">
          <ProgressSpinner />
          <span class="ml-2">Loading coaching configuration…</span>
        </div>

        <div v-else-if="error && !form" class="p-4 bg-red-50 border border-red-200 rounded">
          <p class="text-red-800"><strong>Error:</strong> {{ error }}</p>
          <Button label="Retry" icon="pi pi-refresh" class="p-button-sm mt-2" @click="loadConfig" />
        </div>

        <template v-else-if="form">
          <CoachingConfigFields
            :form="form"
            :prompt-template-defaults="promptTemplateDefaults"
            :dimension-options="dimensionOptions"
            :schema-options="schemaOptions"
            :disabled="loading || saving"
          />

          <div class="flex justify-end gap-2 pt-4 border-t mt-6">
            <Button label="Reset" severity="secondary" :disabled="!hasChanges || saving" @click="resetForm" />
            <Button label="Save" icon="pi pi-save" :loading="saving" :disabled="!hasChanges" @click="saveConfig" />
          </div>
        </template>
      </template>
    </Card>

    <Card class="mt-6">
      <template #title>Assessment baseline bootstrap</template>
      <template #content>
        <p class="text-gray-600 mb-4">
          One-time (or manual re-) import of coaching profile baselines from submitted assessments.
          Uses the saved <strong>Bootstrap assessment schema</strong> filter and coachable dimensions above.
          Save configuration first if you changed those settings. New submissions update baselines automatically after evaluation.
        </p>

        <div v-if="bootstrapResult" class="mb-4 p-4 bg-gray-50 border border-gray-200 rounded text-sm">
          <p class="font-medium text-gray-800 mb-2">Last run</p>
          <ul class="space-y-1 text-gray-700">
            <li>Users processed: {{ bootstrapResult.usersProcessed ?? 0 }}</li>
            <li>Users with baseline: {{ bootstrapResult.usersWithBaseline ?? 0 }}</li>
            <li>Total dimensions written: {{ bootstrapResult.totalDimensions ?? 0 }}</li>
          </ul>
          <div
            v-if="bootstrapResult.errors?.length"
            class="mt-3 p-3 bg-amber-50 border border-amber-200 rounded"
          >
            <p class="font-medium text-amber-900 mb-1">{{ bootstrapResult.errors.length }} error(s)</p>
            <ul class="list-disc list-inside text-amber-800 max-h-32 overflow-y-auto">
              <li v-for="(entry, index) in bootstrapResult.errors" :key="index">
                {{ entry.userId }}: {{ entry.message }}
              </li>
            </ul>
          </div>
        </div>

        <Button
          label="Run assessment baseline bootstrap"
          icon="pi pi-database"
          severity="warn"
          :loading="bootstrapping"
          :disabled="loading || saving"
          @click="confirmBootstrap"
        />
      </template>
    </Card>

    <Card class="mt-6">
      <template #title>Score review queue (UC6)</template>
      <template #content>
        <p class="text-gray-600 mb-4">
          Review flagged LLM scores before they become final on user profiles (depending on review policy).
          No chat transcripts are shown — only score, interpretation, flags, and assessment baseline.
        </p>
        <router-link to="/contentManagement/coaching-score-review">
          <Button
            :label="pendingReviewCount > 0
              ? `Open review queue (${pendingReviewCount} pending)`
              : 'Open review queue'"
            icon="pi pi-check-square"
            severity="help"
          />
        </router-link>
      </template>
    </Card>

    <ConfirmDialog />
  </div>
</template>

<script>
import Card from 'primevue/card';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import ProgressSpinner from 'primevue/progressspinner';
import CoachingConfigFields from '@/components/ContentManagement/common/CoachingConfigFields.vue';
import coachingAdminService from '@/services/coachingAdminService';
import { normalizeCoachingConfigForm } from '@/utils/coachingConfigOptions';

export default {
  name: 'CoachingConfigManager',
  components: {
    Card,
    Button,
    ConfirmDialog,
    ProgressSpinner,
    CoachingConfigFields,
  },
  data() {
    return {
      loading: false,
      saving: false,
      bootstrapping: false,
      bootstrapResult: null,
      error: null,
      form: null,
      originalForm: null,
      promptTemplateDefaults: {},
      dimensionOptions: [],
      schemaOptions: [],
      pendingReviewCount: 0,
    };
  },
  computed: {
    hasChanges() {
      if (!this.form || !this.originalForm) return false;
      return JSON.stringify(this.form) !== JSON.stringify(this.originalForm);
    },
  },
  mounted() {
    this.loadConfig();
    this.loadPendingReviewCount();
  },
  methods: {
    applyView(data) {
      this.promptTemplateDefaults = data?.promptTemplateDefaults ?? {};
      this.form = normalizeCoachingConfigForm(data?.config ?? data, this.promptTemplateDefaults);
      this.originalForm = JSON.parse(JSON.stringify(this.form));
      this.dimensionOptions = data?.dimensionOptions ?? [];
      this.schemaOptions = data?.schemaOptions ?? [];
    },
    async loadConfig() {
      this.loading = true;
      this.error = null;
      try {
        const data = await coachingAdminService.getConfig();
        this.applyView(data);
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Failed to load coaching configuration';
      } finally {
        this.loading = false;
      }
    },
    async loadPendingReviewCount() {
      try {
        const data = await coachingAdminService.listScoreReviews('pending_review');
        this.pendingReviewCount = data.pendingCount ?? 0;
      } catch {
        this.pendingReviewCount = 0;
      }
    },
    resetForm() {
      this.form = JSON.parse(JSON.stringify(this.originalForm));
    },
    async saveConfig() {
      if (!this.hasChanges) return;
      this.saving = true;
      try {
        const payload = {
          ...this.form,
          bootstrapSchemaId: this.form.bootstrapSchemaId || '',
        };
        const data = await coachingAdminService.updateConfig(payload);
        this.applyView(data);
        this.$toast.add({
          severity: 'success',
          summary: 'Saved',
          detail: 'Coaching configuration updated',
          life: 3000,
        });
      } catch (err) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.response?.data?.message || 'Failed to save coaching configuration',
          life: 5000,
        });
      } finally {
        this.saving = false;
      }
    },
    confirmBootstrap() {
      this.$confirm.require({
        message: 'Rebuild assessment baselines for all users with submitted assessments? This may take a while on large tenants.',
        header: 'Run baseline bootstrap',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Run bootstrap',
        rejectLabel: 'Cancel',
        accept: () => this.runBootstrap(),
      });
    },
    async runBootstrap() {
      this.bootstrapping = true;
      try {
        const result = await coachingAdminService.bootstrapAssessmentBaselines();
        this.bootstrapResult = result;
        this.$toast.add({
          severity: result.errors?.length ? 'warn' : 'success',
          summary: 'Bootstrap complete',
          detail: `${result.usersWithBaseline ?? 0} of ${result.usersProcessed ?? 0} user(s) received a baseline`,
          life: 5000,
        });
      } catch (err) {
        this.$toast.add({
          severity: 'error',
          summary: 'Bootstrap failed',
          detail: err?.response?.data?.message || err?.message || 'Failed to run assessment baseline bootstrap',
          life: 6000,
        });
      } finally {
        this.bootstrapping = false;
      }
    },
  },
};
</script>
